import { useState } from "react";
import api from "@/lib/api";
import { Sparkles, Loader2, Wand2, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-fuchsia-500/20 focus:border-fuchsia-500 outline-none transition-all";
const tones = ["energetic", "playful", "luxury", "professional", "bold", "friendly"];
const platforms = ["Instagram", "TikTok", "LinkedIn", "X / Twitter", "Facebook"];

export default function AICopy() {
  const [form, setForm] = useState({ product: "", audience: "", tone: "energetic", platform: "Instagram" });
  const [loading, setLoading] = useState(false);
  const [copy, setCopy] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!form.product.trim() || !form.audience.trim()) return toast.error("Tell us the product and audience first");
    setLoading(true); setCopy("");
    try {
      const { data } = await api.post("/ai/marketing-copy", form);
      setCopy(data.copy);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const doCopy = () => { navigator.clipboard.writeText(copy); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl bg-fuchsia-600 flex items-center justify-center shadow-lg shadow-fuchsia-500/30"><Sparkles className="h-6 w-6 text-white" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">AI Copy Studio</h1>
          <p className="text-slate-500">Generate scroll-stopping marketing copy with Claude.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 space-y-4">
          <div>
            <label className="text-sm font-bold text-slate-700">Product or service</label>
            <input data-testid="ai-product" placeholder="e.g. Vitamin C glow serum" className={`${inputCls} mt-1.5`} value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700">Target audience</label>
            <input data-testid="ai-audience" placeholder="e.g. Gen Z skincare lovers" className={`${inputCls} mt-1.5`} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700">Tone</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {tones.map((t) => (
                <button key={t} data-testid={`tone-${t}`} onClick={() => setForm({ ...form, tone: t })}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize transition-all ${form.tone === t ? "bg-fuchsia-600 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-fuchsia-100"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700">Platform</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {platforms.map((p) => (
                <button key={p} data-testid={`platform-${p}`} onClick={() => setForm({ ...form, platform: p })}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${form.platform === p ? "bg-blue-600 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-blue-100"}`}>{p}</button>
              ))}
            </div>
          </div>
          <button data-testid="ai-generate" onClick={generate} disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white bg-fuchsia-600 hover:bg-fuchsia-700 shadow-lg shadow-fuchsia-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />} Generate copy
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 min-h-[400px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-lg text-slate-900">Your copy</h3>
            {copy && <button data-testid="ai-copy-clipboard" onClick={doCopy} className="flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}</button>}
          </div>
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-fuchsia-400">
              <Loader2 className="h-10 w-10 animate-spin mb-3" /><p className="font-semibold">Crafting your copy…</p>
            </div>
          ) : copy ? (
            <motion.pre data-testid="ai-output" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex-1 whitespace-pre-wrap font-sans text-slate-700 leading-relaxed text-[15px] overflow-y-auto">{copy}</motion.pre>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-300">
              <Sparkles className="h-12 w-12 mb-3" /><p className="font-semibold text-slate-400">Your generated copy will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
